// Screen / Home (Visual Direction), over the sky's lower half with the
// horizon (the sky itself belongs to the scene host): a top bar with the
// Horoscope button top-left and the full screen and menu buttons top-right
// (full screen only where the browser offers it; on a phone it sits below the
// menu button), placed and drawn as in play, the title block, then a column
// with the date row and countdown and one full-width row per level on the
// night's three layers (Daily Structure, Aaron 2026-11-01): the night's own
// rows, a WEEKLY rule with the week's rows (Assemblage XL, Apogee XL), a
// MONTHLY rule with the puzzle packs (a placeholder until they exist), each
// row with a status-carrying button; and the copyright footer. It shows
// tonight at / and a past night at /archive/{date}; a past night has the
// Horoscope button only where a horoscope was written that night, to read it,
// since only tonight's can be written. The Horoscope button carries a star
// while tonight has constellations the Horoscope has not seen and is not yet
// written. While the Horoscope is paused (systems/horoscope/paused.ts) there is
// no Horoscope button. Tonight's home turns to the next night when the countdown ends.
// The menu's Constellations entry holds tonight's constellations on every level.
// The menu's Debug entry appears only where the Worker's debug tools are open.
import { useEffect, useState } from 'react';
import { horoscopePath, matchHoroscopeRoute, matchNightRoute, matchPlayRoute, navigate, playPath, usePath } from '../../app/router';
import type { NightResponse, TodayLevel } from '../../systems/api/types';
import { horoscopeNoticeDue } from '../../systems/horoscope/horoscopeNotice';
import { HOROSCOPE_PAUSED } from '../../systems/horoscope/paused';
import { monthResetAt, serverNow, weekResetAt } from '../../systems/clock/serverClock';
import { loadNight, nightStore } from '../../systems/night/nightStore';
import { canFullscreen, toggleFullscreen } from '../../systems/settings/fullscreen';
import { useStore } from '../../systems/store';
import { IconButton } from '../components/Button';
import { Icon } from '../components/Icon';
import { MenuTray, type MenuEntry } from '../menu/MenuTray';
import { DateRow } from './DateRow';
import { DebugPanel } from './DebugPanel';
import { LevelCard } from './LevelCard';
import { ResetTimer } from './ResetTimer';
import { TonightConstellations, useTonightConstellations } from './TonightConstellations';
import './home.css';

export function HomeScreen() {
  const { phase, night, error, requested } = useStore(nightStore);
  const [menuOpen, setMenuOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [constellationsOpen, setConstellationsOpen] = useState(false);
  const tonight = useTonightConstellations(menuOpen);
  // Home stays mounted while it fades out on the way to play or to the
  // Horoscope; it follows only its own routes.
  const path = usePath();
  const onHome = !matchPlayRoute(path) && !matchHoroscopeRoute(path);
  const routeNight = matchNightRoute(path);

  useEffect(() => {
    if (!onHome) return;
    void loadNight(routeNight);
    const onVisible = () => document.visibilityState === 'visible' && void loadNight(routeNight);
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [onHome, routeNight]);

  // An archive address naming tonight (an old link, or a night that has
  // just begun) is tonight's home.
  useEffect(() => {
    if (onHome && requested && night && night.date === night.tonight) navigate('/', true);
  }, [onHome, requested, night]);

  // At the countdown's end tonight's home turns to the next night. A second's
  // margin for the Worker's clock; if it still answers the old night, the new
  // answer re-arms the timer and it asks again.
  const rolloverAt = !routeNight ? night?.rolloverAt ?? null : null;
  useEffect(() => {
    if (!onHome || rolloverAt === null) return;
    const t = setTimeout(() => void loadNight(null), Math.max(2000, rolloverAt - serverNow() + 1000));
    return () => clearTimeout(t);
  }, [onHome, rolloverAt, night]);

  // Tonight always has the Horoscope; a past night only to read one written then.
  const pastHoroscope = routeNight !== null && night?.date === routeNight && night.horoscopeWritten ? night.date : null;

  // The night's own levels, then its week's; any number of each stacks in its list.
  const daily = night?.daily ?? [];
  const weekly = night?.weekly.levels ?? [];
  const play = (v: TodayLevel) => navigate(playPath(v.levelId, night && night.date !== night.tonight ? night.date : null));

  const entries: Array<MenuEntry | 'divider'> = [
    { key: 'account', label: 'Log in', soon: true },
    { key: 'settings', label: 'Settings', soon: true },
    { key: 'how', label: 'How to play', soon: true },
    { key: 'boards', label: 'Leaderboards', soon: true },
    {
      key: 'constellations',
      label: 'Constellations',
      detail: tonight.data ? String(tonight.data.constellations.length) : undefined,
      content: <TonightConstellations data={tonight.data} failed={tonight.failed} />,
      open: constellationsOpen,
      onSelect: () => setConstellationsOpen(!constellationsOpen),
    },
    { key: 'credits', label: 'Credits', soon: true },
    { key: 'contact', label: 'Contact', soon: true },
    'divider',
    { key: 'privacy', label: 'Privacy', small: true, soon: true },
    { key: 'terms', label: 'Terms', small: true, soon: true },
    { key: 'impressum', label: 'Impressum', small: true, soon: true },
  ];
  if (night?.debug) {
    entries.push('divider', { key: 'debug', label: 'Debug', content: <DebugPanel night={night} />, open: debugOpen, onSelect: () => setDebugOpen(!debugOpen) });
  }

  return (
    <div className="home">
      <header className="home-header">
        {!HOROSCOPE_PAUSED && (routeNight === null || pastHoroscope) && <HoroscopeButton date={pastHoroscope} notice={horoscopeNoticeDue(night)} />}
        <div className="home-header-right">
          {canFullscreen() && <IconButton icon="fullscreen" label="Full screen" className="panel fullscreen-button" onClick={() => void toggleFullscreen()} />}
          <IconButton icon="menu" label="Menu" className="panel" onClick={() => setMenuOpen(true)} />
        </div>
      </header>
      <main className="home-main">
        <div className="title-block">
          <h1 className="title">Written in the Stars</h1>
          <p className="subtitle quiet">A nightly word puzzle</p>
        </div>
        <div className="home-column">
          <DateRow night={night} requested={requested} />
          {phase === 'error' && (
            <div className="panel home-notice">
              <p>{error}</p>
              {requested ? (
                <button type="button" className="btn btn-ghost btn-small" onClick={() => navigate('/')}>
                  Back to tonight
                </button>
              ) : (
                <button type="button" className="btn btn-ghost btn-small" onClick={() => void loadNight(null)}>
                  Try again
                </button>
              )}
            </div>
          )}
          {!night && phase !== 'error' && <p className="quiet level-loading">Reading tonight's sky…</p>}
          {daily.length > 0 && (
            <div className="level-list">
              {daily.map((v) => (
                <LevelCard key={v.levelId} level={v} onPlay={() => play(v)} />
              ))}
            </div>
          )}
          {night && daily.length === 0 && <p className="quiet level-placeholder">No levels on this night.</p>}
          {night && weekly.length > 0 && (
            <>
              <div className="layer-divider" role="separator" aria-label="This week's levels">
                <span className="label-mono">Weekly</span>
                <ResetTimer resetAt={weekResetAt(night.weekly.weekStart)} title="The weekly puzzles reset on Monday at 00:00 UTC." />
              </div>
              <div className="level-list">
                {weekly.map((v) => (
                  <LevelCard key={v.levelId} level={v} onPlay={() => play(v)} />
                ))}
              </div>
            </>
          )}
          {night && (
            <>
              <div className="layer-divider" role="separator" aria-label="This month's puzzle packs">
                <span className="label-mono">Monthly</span>
                <ResetTimer resetAt={monthResetAt(night.monthly.month)} title="The monthly puzzles reset on the 1st of the month at 00:00 UTC." />
              </div>
              <p className="quiet level-placeholder">Puzzle packs are coming.</p>
            </>
          )}
        </div>
        <footer className="home-footer quiet">© 2026 Paul Schulz &amp; Aaron Vanderbeek</footer>
      </main>
      <MenuTray open={menuOpen} entries={entries} onClose={() => setMenuOpen(false)} />
    </div>
  );
}

/** The Horoscope, never locked (the screen says what it still needs); a past night's, `date`, only to read. */
function HoroscopeButton({ date, notice }: { date: string | null; notice: boolean }) {
  return (
    <button
      type="button"
      className="icon-btn panel horoscope-button"
      aria-label={notice ? 'Horoscope, new constellations' : 'Horoscope'}
      title="Horoscope"
      onClick={() => navigate(horoscopePath(date))}
    >
      <Icon name="horoscope" />
      {notice && <Icon name="star-4" className="horoscope-notice" />}
    </button>
  );
}

export type { NightResponse };
